import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { PeriodModal } from './components';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { useWeekAgenda, useWeekAgendaFunctions } from './contexts/WeekAgendaContext';
import ActivityDragCard from './components/ActivityDragCard';
import DefaultButton from 'components/DefaultButton';
import DefaultModal from 'components/DefaultModal';
import { Draggable } from '@fullcalendar/interaction';
import routes from 'config/routes';
import StyledAgenda from '../StyledAgenda';
import useStyles from './styles';

const initialSelectedEvent = {
  id: null,
  dayName: '',
  endTime: '',
  startTime: '',
  title: '',
  is_blocked: false,
};

const WeekAgenda = ({ monitor, onStepChange, stepsLoading }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const { activities, activitiesLoading, agendaLoading, appointmentState, currentAppointments } = useWeekAgenda();
  const { saveAppointments, savePeriod, setAppointmentState, setCurrentAppointments } = useWeekAgendaFunctions();

  const [selectedEvent, setSelectedEvent] = useState(initialSelectedEvent);
  const [saved, setSaved] = useState(false);

  const [draggableInitialized, setDraggableInitialized] = useState(false);

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [openPeriodModal, setOpenPeriodModal] = useState(false);

  const [isSavingWarning, setIsSavingWarning] = useState(false);

  const noActivities = useMemo(
    () => monitor && !activitiesLoading && activities.length === 0,
    [activities, activitiesLoading, monitor],
  );
  const [showNoActivityDialog, setShowNoActivityDialog] = useState(noActivities);

  const appointmentsChanged = useMemo(() => {
    const { createdAppointments, removedAppointments, updatedAppointments } = appointmentState;
    return createdAppointments.length !== 0 || removedAppointments.length !== 0 || updatedAppointments.length !== 0;
  }, [appointmentState]);

  let calendarRef = useRef();
  const isLoading = agendaLoading || stepsLoading;

  useEffect(() => {
    setShowNoActivityDialog(noActivities);
  }, [noActivities]);

  useEffect(() => {
    if (currentAppointments && currentAppointments.monitor?.id !== monitor?.id) setOpenWarningDialog(true);
  }, [currentAppointments, monitor?.id]);

  useEffect(() => {
    if (activities && !draggableInitialized) {
      setDraggableInitialized(true);
      const element = document.getElementById('servicesListContainer');
      if (element) {
        new Draggable(element, {
          itemSelector: '#serviceCard',
          eventData: (eventElement) => {
            return JSON.parse(eventElement.getAttribute('data-event'));
          },
        });
      }
    }
  }, [activities, draggableInitialized]);

  const checkEvent = (_, event) => {
    return !event.extendedProps.is_blocked;
  };

  const onAppointmentClick = (e) => {
    // Waiting for the sendAgenda call from onEventReceive to end
    if (e.event.id === '') {
      return;
    }

    e.cancel = true;
    let startDate = new Date(e.event.start);
    let startTime = format(startDate, 'HH:mm');
    let endTime = format(new Date(e.event.end), 'HH:mm');
    setSelectedEvent({
      id: e.event.id,
      dayName: startDate.toLocaleString('fr-fr', { weekday: 'long' }),
      endTime,
      startTime,
      title: e.event.title,
      is_blocked: e.event.extendedProps.is_blocked,
    });
    setOpenDetailsDialog(true);
  };

  const onEventReceive = ({ event }) => {
    const newStartDate = new Date(event.start);
    const newEndDate = new Date(event.end);
    const newAppointment = {
      id: `created-${appointmentState.createdAppointments.length}`,
      daysOfWeek: [newStartDate.getDay()],
      endTime: format(newEndDate, 'HH:mm'),
      service_id: event.extendedProps.service_id,
      startTime: format(newStartDate, 'HH:mm'),
      title: event.title,
    };

    event.setProp('id', `created-${appointmentState.createdAppointments.length}`);
    event.startDate = newStartDate;
    event.endDate = newEndDate;

    // Add appointment to created list
    let newCreatedItems = [...appointmentState.createdAppointments];
    newCreatedItems.push(newAppointment);

    const appointments = { ...appointmentState, createdAppointments: newCreatedItems };
    setAppointmentState(appointments);
    setCurrentAppointments({ ...appointments, monitor });
  };

  const onEventChange = ({ oldEvent, event }) => {
    if (!oldEvent.id) return;

    const newStartDate = new Date(event.start);
    const newEndDate = new Date(event.end);
    const newAppointment = {
      id: event.id,
      daysOfWeek: [newStartDate.getDay()],
      endTime: format(newEndDate, 'HH:mm'),
      service_id: event.extendedProps.service_id,
      startTime: format(newStartDate, 'HH:mm'),
      title: event.title,
    };

    // Replace appointment in general list
    const indexApp = appointmentState.appointments.findIndex((e) => e.id === newAppointment.id);
    let newItems = [...appointmentState.appointments];
    if (indexApp !== -1) newItems.splice(indexApp, 1, newAppointment);

    // If appointment in created list, replace it there
    const indexCreated = appointmentState.createdAppointments.findIndex((e) => e.id === newAppointment.id);
    let newCreatedItems = [...appointmentState.createdAppointments];
    if (indexCreated !== -1) newCreatedItems.splice(indexCreated, 1, newAppointment);

    // If appointment already in updated list, replace it otherwise add it if it's not previously created
    const indexUpdated = appointmentState.updatedAppointments.findIndex((e) => e.id === newAppointment.id);
    let newUpdatedItems = [...appointmentState.updatedAppointments];
    if (indexUpdated !== -1) newUpdatedItems.splice(indexUpdated, 1, newAppointment);
    else if (indexCreated === -1) newUpdatedItems.push(newAppointment);

    const appointments = {
      ...appointmentState,
      appointments: newItems,
      createdAppointments: newCreatedItems,
      updatedAppointments: newUpdatedItems,
    };
    setAppointmentState(appointments);
    setCurrentAppointments({ ...appointments, monitor });
  };

  const onRemoveEvent = () => {
    if (selectedEvent.is_blocked) return;

    // Remove appointment from general list
    const appointment = appointmentState.appointments.find((e) => e.id === selectedEvent.id);
    let newItems = [...appointmentState.appointments];
    if (appointment) newItems = newItems.filter((i) => i.id !== appointment.id);

    // Remove appointment from created list if exists
    const createdAppointment = appointmentState.createdAppointments.find((e) => e.id === selectedEvent.id);
    let newCreatedItems = [...appointmentState.createdAppointments];
    if (createdAppointment) newCreatedItems = newCreatedItems.filter((i) => i.id !== createdAppointment.id);

    // Remove appointment from updated list if exists
    const updatedAppointment = appointmentState.updatedAppointments.find((e) => e.id === selectedEvent.id);
    let newUpdatedItems = [...appointmentState.updatedAppointments];
    if (updatedAppointment) newUpdatedItems = newUpdatedItems.filter((i) => i.id !== updatedAppointment.id);

    // Add appointment to removed list
    const removedAppointment = appointment || updatedAppointment;
    let newRemovedItems = [...appointmentState.removedAppointments];
    if (removedAppointment) newRemovedItems = [...appointmentState.removedAppointments, removedAppointment];

    const appointments = {
      ...appointmentState,
      appointments: newItems,
      createdAppointments: newCreatedItems,
      removedAppointments: newRemovedItems,
      updatedAppointments: newUpdatedItems,
    };
    setAppointmentState(appointments);
    setCurrentAppointments({ ...appointments, monitor });

    let calendarApi = calendarRef.current.getApi();
    let calendarEvent = calendarApi.getEventById(selectedEvent.id);
    if (calendarEvent) calendarEvent.remove();
    setOpenDetailsDialog(false);
  };

  const handleCloseNoActivityDialog = () => {
    setShowNoActivityDialog(false);
  };

  const handleConfirmNoActivityDialog = () => {
    history.push(routes.activities.root);
  };

  // Detailed event dialog handlers
  const closeDetailedDialog = () => {
    setOpenDetailsDialog(false);
    setTimeout(() => setSelectedEvent(initialSelectedEvent), 200);
  };

  const handleWarningSaveClick = async () => {
    setIsSavingWarning(true);
    const saved = await saveAppointments(currentAppointments);
    if (saved) {
      setCurrentAppointments(null);
      setOpenWarningDialog(false);
    }
    setIsSavingWarning(false);
  };

  const handleWarningClose = () => {
    setOpenWarningDialog(false);
    setCurrentAppointments(null);
  };

  const handleCancelAgenda = () => {
    if (!!history.location.state?.origin) {
      history.push(history.location.state.origin, { origin: null });
    } else {
      history.push(routes.home.root, { origin: null });
    }
  };

  const handleValidateAgenda = () => {
    onStepChange(1);
    setOpenPeriodModal(true);
  };

  const handleCancelPeriod = () => {
    setOpenPeriodModal(false);
    onStepChange(0);
  };

  // Save appointments and period
  const handleSubmitPeriod = async (values) => {
    const savedAppointments = await saveAppointments(appointmentState);
    if (savedAppointments) {
      const savedPeriod = await savePeriod(values);
      if (savedPeriod) {
        setSaved(savedPeriod);
        setTimeout(() => {
          if (location.state?.showEnd) history.push(routes.end.root, undefined);
          else history.push(routes.agenda.root, undefined);
        }, 100);
      }
    }
  };

  return (
    <Grid container spacing={3}>
      {!isLoading && (
        <Typography style={{ fontSize: 14, fontStyle: 'italic', marginBottom: '8px', marginLeft: '16px' }}>
          Attention : les créneaux concernant des activités bloquées ne peuvent pas être modifiés !
        </Typography>
      )}

      <Grid item xs={9}>
        {isLoading && (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50vh',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (
          <StyledAgenda
            ref={calendarRef}
            events={appointmentState.appointments}
            initialView="timeGridWeek"
            forceEventDuration={true}
            editable={true}
            droppable={true}
            eventAllow={checkEvent}
            eventClick={onAppointmentClick}
            eventReceive={onEventReceive}
            eventChange={onEventChange}
            config
          />
        )}
      </Grid>

      <Grid item xs={3}>
        <div id="servicesListContainer">
          <>
            {activitiesLoading && <CircularProgress />}
            {!activitiesLoading &&
              activities.map((activity, index) => (
                <ActivityDragCard key={`activity-drag-card-${index}`} activity={activity} />
              ))}
          </>
        </div>
      </Grid>

      <Grid container item xs={12} alignItems="center" justifyContent="center">
        <DefaultButton cancel onClick={handleCancelAgenda}>
          Annuler
        </DefaultButton>
        <DefaultButton
          color="secondary"
          disabled={!appointmentsChanged}
          variant="contained"
          onClick={handleValidateAgenda}
        >
          Valider
        </DefaultButton>
      </Grid>

      <DefaultModal
        cancelButtonText="Fermer"
        confirmButtonText="Créer une activité"
        forceConfirm
        onClose={handleCloseNoActivityDialog}
        onConfirm={handleConfirmNoActivityDialog}
        open={showNoActivityDialog}
        title="Attention"
        square
      >
        <Box className={classes.modalContent}>
          <Typography className={classes.modalText} align="center">
            Vous devez paramétrer au moins une activité pour
            <span className={classes.primaryText}>
              {' ' + monitor.first_name} {monitor.last_name + ' '}
            </span>
            dans
            <span className={classes.primaryText}> Activités</span>
          </Typography>
        </Box>
      </DefaultModal>

      <DefaultModal
        cancelButtonText="Fermer"
        confirmButtonText="Supprimer"
        disableConfirm={selectedEvent.is_blocked}
        onClose={closeDetailedDialog}
        onConfirm={onRemoveEvent}
        open={openDetailsDialog}
        title="Suppression d'un créneau"
        square
      >
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h4" style={{ padding: '16px' }}>
            {selectedEvent.title}
          </Typography>
          <Typography style={{ fontSize: 18, fontWeight: 300 }}>
            {`${selectedEvent.dayName} de ${selectedEvent.startTime} à ${selectedEvent.endTime}h`}
          </Typography>
        </Box>
      </DefaultModal>

      <DefaultModal forceConfirm hideActions open={openWarningDialog} title="Attention" square>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            paddingBottom: '16px',
          }}
        >
          <Box className={classes.modalContent} style={{ paddingBottom: '16px' }}>
            <Typography className={classes.modalText} align="center">
              L'agenda de
              <span className={classes.primaryText}>
                {' '}
                {currentAppointments?.monitor?.first_name} {currentAppointments?.monitor?.last_name}{' '}
              </span>
              a été modifié, voulez-vous sauvegarder les modifications ?
            </Typography>
          </Box>

          <Box style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <DefaultButton cancel onClick={handleWarningClose}>
              Non
            </DefaultButton>
            <DefaultButton disabled={isSavingWarning} standard variant="contained" onClick={handleWarningSaveClick}>
              {isSavingWarning ? <CircularProgress size={20} /> : 'Sauvegarder'}
            </DefaultButton>
          </Box>
        </Box>
      </DefaultModal>

      <PeriodModal open={openPeriodModal} onCancel={handleCancelPeriod} onSubmit={handleSubmitPeriod} />

      <Prompt
        when={!saved && appointmentsChanged}
        message={"Les modifications n'ont pas été enregistrées, voulez-vous tout de même continuer ?"}
      />
    </Grid>
  );
};

export default WeekAgenda;
